import { useCallback, useEffect, useState } from 'react';
import { Client } from '../models/table-models';
import { NewRequestBranch } from '../models/dashboard-models';
import { useGetClient as fetchClient, useGetBranchByIdInRequestView as fetchBranch } from './clients-hooks';
import { normalizeRequestId } from './request-selection';

// Server snapshots are keyed by identity; form drafts never enter this cache.
export const useRequestEntities = (clientId?: number, branchId?: number) => {
    const [clients, setClients] = useState<Record<number, Client>>({});
    const [branches, setBranches] = useState<Record<number, NewRequestBranch>>({});
    // IDs whose restoration failed; cleared by retry.
    const [failed, setFailed] = useState<{ clientId?: number; branchId?: number }>({});
    const [attempt, setAttempt] = useState(0);
    const updateClient = useCallback((client: Client) => {
        const id = normalizeRequestId(client.id);
        if (id) setClients(current => ({ ...current, [id]: { ...client, id } }));
    }, []);
    const updateBranch = useCallback((branch: NewRequestBranch) => {
        const id = normalizeRequestId(branch.id);
        if (id) setBranches(current => ({ ...current, [id]: { ...branch, id } }));
    }, []);
    const retry = useCallback(() => {
        setFailed({});
        setAttempt(value => value + 1);
    }, []);
    const client = clientId ? clients[clientId] : undefined;
    const branch = branchId ? branches[branchId] : undefined;

    useEffect(() => {
        if (!clientId || client) return;
        let active = true;
        fetchClient(String(clientId)).then(value => {
            if (!active) return;
            // A forbidden read resolves with {} instead of rejecting.
            if (!normalizeRequestId(value?.id)) throw new Error(`Client ${clientId} is not available`);
            updateClient(value);
        }).catch(error => {
            if (!active) return;
            console.error('Failed to restore client', error);
            setFailed(current => ({ ...current, clientId }));
        });
        return () => { active = false; };
    }, [clientId, client, updateClient, attempt]);

    useEffect(() => {
        if (!branchId || branch) return;
        let active = true;
        fetchBranch(branchId).then(value => {
            if (!active) return;
            if (!normalizeRequestId(value?.id)) throw new Error(`Branch ${branchId} is not available`);
            updateBranch(value);
        }).catch(error => {
            if (!active) return;
            console.error('Failed to restore branch', error);
            setFailed(current => ({ ...current, branchId }));
        });
        return () => { active = false; };
    }, [branchId, branch, updateBranch, attempt]);

    const loadFailed = Boolean((clientId && !client && failed.clientId === clientId)
        || (branchId && !branch && failed.branchId === branchId));

    return { client, branch, updateClient, updateBranch, loadFailed, retry };
};
